/*
 * The closed filing_status labels, supplied by the API as closed_statuses and set
 * once per response. This module used to keep its own list, which is why the tab
 * count and the rows beneath it described different sets: 12 open groups against 20
 * rows. The labels it held were BIS vocabulary (SIGNED-OFF, SIGNOFF, SUSPENDED) that
 * the DOB NOW dataset never emits, plus an alias map that rewrote real labels such
 * as FILING WITHDRAWN into absent ones.
 *
 * Empty until a response arrives. Nothing classifies before then, and setting it is
 * how the browser and the server stay one classification rather than two.
 */
let CLOSED_STATUSES = new Set();

/** @param {string[]} statuses uppercased, trimmed labels from the API */
export function setDobNowClosedStatuses(statuses) {
	CLOSED_STATUSES = new Set(
		(Array.isArray(statuses) ? statuses : []).map((s) => String(s).trim().toUpperCase())
	);
}

export function getDobNowClosedStatuses() {
	return [...CLOSED_STATUSES];
}

const WORK_TYPE_FIELD_CODES = [
	['general_construction_work_type_', 'GC'],
	['plumbing_work_type', 'PL'],
	['mechanical_systems_work_type_', 'MS'],
	['structural_work_type_', 'ST'],
	['sprinkler_work_type', 'SP'],
	['earth_work_work_type_', 'EW'],
	['foundation_work_type_', 'FN'],
	['boiler_equipment_work_type_', 'BL'],
	['solar_work_type_', 'SL'],
	['full_demolition_work_type_', 'DM'],
	['place_of_assembly_work_type_', 'PA'],
	['protection_mechanical_methods_work_type_', 'PM'],
	['sidewalk_shed_work_type_', 'SS'],
	['support_of_excavation_work_type_', 'SE'],
	['temporary_place_of_assembly_work_type_', 'TP'],
	['green_roof_work_type_', 'GR'],
	['suspended_scaffold_work_type_', 'SF'],
];

const BOOLEAN_WORK_TYPE_FIELD_CODES = [
	['scaffold', 'SC'],
	['sign', 'SG'],
	['shed', 'SD'],
	['standpipe', 'STP'],
	['antenna', 'AN'],
	['curb_cut', 'CC'],
	['fence', 'FC'],
];

function isTruthyFieldValue(value) {
	const normalized = String(value ?? '')
		.trim()
		.toUpperCase();
	return normalized === 'YES' || normalized === 'TRUE' || normalized === 'Y';
}

const DOB_NOW_GROUPS_PER_PAGE = 20;

export function parseJobFilingNumber(jobFilingNumber) {
	if (!jobFilingNumber) {
		return { jobNumber: '', filingDesignation: '' };
	}

	const value = String(jobFilingNumber).trim();
	const dashIndex = value.lastIndexOf('-');
	if (dashIndex === -1) {
		return { jobNumber: value, filingDesignation: '' };
	}

	const jobNumber = value.slice(0, dashIndex);
	const filingDesignation = value.slice(dashIndex + 1).toUpperCase();
	return { jobNumber, filingDesignation };
}

/**
 * Uppercase and trim, matching how the server compares the same labels. No alias
 * rewriting: the old map turned real labels into absent ones, so FILING WITHDRAWN
 * became WITHDRAWN and then matched nothing.
 */
export function normalizeDobNowStatus(rawStatus) {
	if (rawStatus === null || rawStatus === undefined) {
		return '';
	}

	return String(rawStatus).trim().toUpperCase();
}

export function getDisplayStatus(rawStatus) {
	const normalized = normalizeDobNowStatus(rawStatus);
	return normalized || rawStatus || '-';
}

/*
 * Resolved means the label is on the server's closed list. Open is its exact
 * complement, because the model classifies by a closed list and treats anything
 * absent from it as open, so these two cannot disagree with each other or with the
 * server. A blank status stays resolved, as before.
 */
export function isResolvedStatus(rawStatus) {
	const normalized = normalizeDobNowStatus(rawStatus);
	if (!normalized) {
		return true;
	}
	return CLOSED_STATUSES.has(normalized);
}

export function isOpenStatus(rawStatus) {
	const normalized = normalizeDobNowStatus(rawStatus);
	return normalized !== '' && !CLOSED_STATUSES.has(normalized);
}

export function isFilingDesignation(designation, prefix) {
	return new RegExp(`^${prefix}\\d+$`, 'i').test(designation || '');
}

export function parseDesignationNumber(designation) {
	const match = String(designation || '').match(/\d+/);
	return match ? parseInt(match[0], 10) : 0;
}

export function getWorkType(row) {
	if (row?.work_type && row.work_type !== '-') {
		return row.work_type;
	}

	for (const [field, code] of WORK_TYPE_FIELD_CODES) {
		if (isTruthyFieldValue(row?.[field])) {
			return code;
		}
	}

	for (const [field, code] of BOOLEAN_WORK_TYPE_FIELD_CODES) {
		if (isTruthyFieldValue(row?.[field])) {
			return code;
		}
	}

	return '-';
}

export function getJobType(row) {
	const rawJobType = row?.job_type;
	if (!rawJobType) {
		return '-';
	}

	return String(rawJobType).trim() || '-';
}

function joinNameParts(...parts) {
	return parts
		.map((part) => String(part || '').trim())
		.filter(Boolean)
		.join(' ');
}

export function getBusinessName(row) {
	const candidates = [
		row?.owner_s_business_name,
		row?.filing_representative_business_name,
		joinNameParts(row?.applicant_first_name, row?.applicant_last_name),
		joinNameParts(
			row?.filing_representative_first_name,
			row?.filing_representative_last_name
		),
	];

	for (const candidate of candidates) {
		const value = String(candidate || '').trim();
		if (value) {
			return value;
		}
	}

	return '-';
}

export function getFilingStatusDate(row) {
	const candidates = [
		row?.filing_date,
		row?.current_status_date,
		row?.filing_status_date,
		row?.created_at,
	];

	for (const candidate of candidates) {
		const value = candidate == null ? '' : String(candidate).trim();
		if (value) {
			return candidate;
		}
	}

	return null;
}

export function enrichDobNowFiling(row) {
	const { jobNumber, filingDesignation } = parseJobFilingNumber(row.job_filing_number);
	const normalizedStatus = normalizeDobNowStatus(row.filing_status);

	return {
		...row,
		job_number: row.job_number || jobNumber,
		filing_designation: row.filing_designation || filingDesignation,
		owner_s_business_name: getBusinessName(row),
		job_type: getJobType(row),
		work_type: getWorkType(row),
		filing_status_date: getFilingStatusDate(row),
		displayStatus: getDisplayStatus(row.filing_status),
		filingIsOpen: isOpenStatus(row.filing_status),
		applicationIsOpen: isOpenStatus(row.filing_status) ? 1 : 0,
		normalizedStatus,
	};
}

function getInitialFiling(filings) {
	return filings.find((f) => f.filing_designation === 'I1') || null;
}

function getSubsequentFilings(filings) {
	return filings
		.filter((f) => isFilingDesignation(f.filing_designation, 'S'))
		.sort(
			(a, b) =>
				parseDesignationNumber(a.filing_designation) -
				parseDesignationNumber(b.filing_designation)
		);
}

function getLastSubsequentFiling(filings) {
	const subsequents = getSubsequentFilings(filings);
	return subsequents.length ? subsequents[subsequents.length - 1] : null;
}

export function getDobNowApplicationStatus(filings) {
	const i1 = getInitialFiling(filings);
	const lastS = getLastSubsequentFiling(filings);

	if (i1 && isOpenStatus(i1.filing_status)) {
		return 'open';
	}

	if (lastS && isOpenStatus(lastS.filing_status)) {
		return 'open';
	}

	if (i1 && lastS) {
		if (isResolvedStatus(i1.filing_status) && isResolvedStatus(lastS.filing_status)) {
			return 'resolved';
		}
		return 'open';
	}

	if (i1 && !lastS) {
		return isResolvedStatus(i1.filing_status) ? 'resolved' : 'open';
	}

	if (!i1 && lastS) {
		return isResolvedStatus(lastS.filing_status) ? 'resolved' : 'open';
	}

	return 'open';
}

export function needsDobNowCloseWarning(filings) {
	const i1 = getInitialFiling(filings);
	const lastS = getLastSubsequentFiling(filings);
	const anchor = lastS || i1;
	if (!anchor) {
		return false;
	}

	const paas = filings.filter((f) => isFilingDesignation(f.filing_designation, 'P'));
	if (paas.length === 0) {
		return false;
	}

	const lastPaa = paas.sort(
		(a, b) =>
			parseDesignationNumber(b.filing_designation) -
			parseDesignationNumber(a.filing_designation)
	)[0];

	return isOpenStatus(anchor.filing_status) && normalizeDobNowStatus(lastPaa.filing_status) === 'APPROVED';
}

function buildLineagesForJob(filings) {
	const byWorkType = new Map();

	filings.forEach((filing) => {
		const workType = filing.work_type || '-';
		if (!byWorkType.has(workType)) {
			byWorkType.set(workType, []);
		}
		byWorkType.get(workType).push(filing);
	});

	const lineages = [];

	byWorkType.forEach((typeFilings, workType) => {
		const initial = typeFilings.find((f) => f.filing_designation === 'I1');
		const subsequents = typeFilings
			.filter((f) => isFilingDesignation(f.filing_designation, 'S'))
			.sort(
				(a, b) =>
					parseDesignationNumber(a.filing_designation) -
					parseDesignationNumber(b.filing_designation)
			);
		const paas = typeFilings
			.filter((f) => isFilingDesignation(f.filing_designation, 'P'))
			.sort(
				(a, b) =>
					parseDesignationNumber(a.filing_designation) -
					parseDesignationNumber(b.filing_designation)
			);

		if (initial || subsequents.length) {
			if (initial) {
				lineages.push({
					workType,
					anchorDesignation: 'I1',
					anchorOrder: 0,
					filings: [initial, ...paas],
				});
			}

			subsequents.forEach((subsequent) => {
				lineages.push({
					workType,
					anchorDesignation: subsequent.filing_designation,
					anchorOrder: parseDesignationNumber(subsequent.filing_designation),
					filings: [subsequent, ...paas],
				});
			});
			return;
		}

		if (paas.length) {
			lineages.push({
				workType,
				anchorDesignation: paas[0].filing_designation,
				anchorOrder: 999,
				filings: paas,
			});
		}
	});

	lineages.sort((a, b) => {
		if (a.anchorDesignation === 'I1' && b.anchorDesignation !== 'I1') {
			return -1;
		}
		if (b.anchorDesignation === 'I1' && a.anchorDesignation !== 'I1') {
			return 1;
		}
		return a.anchorOrder - b.anchorOrder;
	});

	return lineages;
}

export function groupDobNowJobApplications(rows) {
	if (!Array.isArray(rows) || rows.length === 0) {
		return [];
	}

	const groupsByJob = new Map();

	rows.forEach((row) => {
		const enriched = enrichDobNowFiling(row);
		const jobNumber = enriched.job_number;
		if (!jobNumber) {
			return;
		}

		if (!groupsByJob.has(jobNumber)) {
			groupsByJob.set(jobNumber, []);
		}
		groupsByJob.get(jobNumber).push(enriched);
	});

	const groups = Array.from(groupsByJob.entries()).map(([jobNumber, filings]) => {
		const lineages = buildLineagesForJob(filings);
		const seenIds = new Set();
		const orderedFilings = [];
		lineages.forEach((lineage) => {
			lineage.filings.forEach((filing) => {
				const key = filing.id || filing.job_filing_number;
				if (seenIds.has(key)) {
					return;
				}
				seenIds.add(key);
				orderedFilings.push(filing);
			});
		});
		const uniqueFilings = orderedFilings.length
			? orderedFilings
			: [...filings].sort((a, b) =>
					a.filing_designation.localeCompare(b.filing_designation)
				);

		const applicationStatus = getDobNowApplicationStatus(filings);
		const businessName =
			filings.map((f) => getBusinessName(f)).find((name) => name !== '-') || '-';

		return {
			jobNumber,
			businessName,
			applicationStatus,
			needsCloseWarning: needsDobNowCloseWarning(filings),
			lineages,
			filings: uniqueFilings,
		};
	});

	return groups.sort((a, b) => String(a.jobNumber).localeCompare(String(b.jobNumber)));
}

export function filterDobNowJobApplicationGroups(groups, filteredStatus) {
	if (!filteredStatus || filteredStatus === 'all') {
		return groups;
	}

	if (filteredStatus === 'open') {
		return groups.filter((group) => group.applicationStatus === 'open');
	}

	if (filteredStatus === 'closed') {
		return groups.filter((group) => group.applicationStatus === 'resolved');
	}

	return groups;
}

export function countDobNowJobApplicationGroups(groups) {
	const numOpen = groups.filter((g) => g.applicationStatus === 'open').length;
	const numClosed = groups.filter((g) => g.applicationStatus === 'resolved').length;
	return {
		numOpen,
		numClosed,
		numCombined: groups.length,
	};
}

export function paginateDobNowGroups(groups, page, perPage = DOB_NOW_GROUPS_PER_PAGE) {
	const totalPages = Math.max(1, Math.ceil(groups.length / perPage));
	const safePage = Math.min(Math.max(page, 1), totalPages);
	const start = (safePage - 1) * perPage;

	return {
		groups: groups.slice(start, start + perPage),
		currentPage: safePage,
		totalPages,
	};
}

export { DOB_NOW_GROUPS_PER_PAGE };
